import { Cpu, Hand, User } from "lucide-react";
import React from "react";

import { HoverTooltip } from "@/common/components/tooltip/HoverTooltip";
import { isAutomationCase } from "@/pages/scenarios/all-scenarios/folder/matchesScriptType";
import { Badge } from "@/components/ui/badge";

interface TestCaseTooltipProps {
  tc: any;
  children: React.ReactNode;
  triggerClassName?: string;
  /** Left offset as a ratio of the trigger width, 0–1 (default 0.7). */
  alignRatio?: number;
  /** Arrow position as a fraction of the card width, 0–1 (default 0.2). */
  arrowCardRatio?: number;
  /** Whether to disable the tooltip */
  disabled?: boolean;
}

function formatStatusLabel(status?: any): string {
  if (!status) return "Ready";
  const s = String(status).toUpperCase();
  if (s === "STATUS_COMPLETED" || s === "COMPLETED") return "Ready";
  if (s.includes("READY") || s.includes("ACTIVE")) return "Ready";
  if (s.includes("PASS")) return "Passed";
  if (s.includes("FAIL")) return "Failed";
  if (s.includes("REWORK")) return "Rework";
  if (s.includes("REVIEW")) return "In Review";
  if (s.includes("PROGRESS")) return "In Progress";
  if (s.includes("OBSOLETE")) return "Obsolete";
  if (s.includes("BLOCK")) return "Blocked";
  if (s.includes("DRAFT")) return "Draft";
  return String(status)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function statusColor(status?: any): string {
  const s = String(status ?? "").toUpperCase();
  if (s === "STATUS_COMPLETED" || s === "COMPLETED") return "text-success";
  if (s.includes("READY") || s.includes("PASS") || s.includes("ACTIVE"))
    return "text-success";
  if (s.includes("REWORK") || s.includes("REVIEW") || s.includes("PROGRESS"))
    return "text-warning";
  if (s.includes("FAIL") || s.includes("OBSOLETE") || s.includes("BLOCK"))
    return "text-destructive";
  return "text-muted-foreground";
}

function statusDotClass(status?: any): string {
  const s = String(status ?? "").toUpperCase();
  if (s === "STATUS_COMPLETED" || s === "COMPLETED") return "bg-success";
  if (s.includes("READY") || s.includes("PASS") || s.includes("ACTIVE"))
    return "bg-success";
  if (s.includes("REWORK") || s.includes("REVIEW") || s.includes("PROGRESS"))
    return "bg-warning";
  if (s.includes("FAIL") || s.includes("OBSOLETE") || s.includes("BLOCK"))
    return "bg-destructive";
  return "bg-success";
}

export function TestCaseTooltip({
  tc,
  children,
  triggerClassName = "inline-flex",
  alignRatio = 0.7,
  arrowCardRatio = 0.2,
  disabled = false,
}: TestCaseTooltipProps) {
  if (disabled) {
    return <>{children}</>;
  }
  const isAuto = isAutomationCase(tc);
  const effectiveStatus =
    tc.status ??
    tc.test_case_status ??
    tc.testCaseStatus ??
    tc.caseStatus ??
    (isAuto ? "READY" : "Ready");
  const tags: string[] = tc.tags
    ? Array.isArray(tc.tags)
      ? tc.tags
          .map((t: any) => {
            if (typeof t === "string") return t;
            if (typeof t === "object" && t !== null)
              return String(t.name || t.label || t.id || "");
            return String(t ?? "");
          })
          .filter(Boolean)
      : String(tc.tags)
          .split(",")
          .map((t: string) => t.trim())
          .filter(Boolean)
    : [];
  const createdByRaw = tc.createdByName || tc.createdBy || tc.creator || "";
  const createdBy = (() => {
    if (!createdByRaw) return "";
    if (typeof createdByRaw === "string") return createdByRaw;
    if (typeof createdByRaw === "object") {
      const val =
        createdByRaw.name || createdByRaw.fullName || createdByRaw.email || "";
      return typeof val === "string" ? val : String(val ?? "");
    }
    return String(createdByRaw);
  })();
  const lastRunRaw = tc.lastRun || tc.result || "";
  const lastRun = (() => {
    if (!lastRunRaw) return "";
    if (typeof lastRunRaw === "string") return lastRunRaw;
    if (typeof lastRunRaw === "object") {
      const val =
        lastRunRaw.result || lastRunRaw.status || lastRunRaw.name || "";
      return typeof val === "string" ? val : String(val ?? "");
    }
    return String(lastRunRaw);
  })();

  const description = (() => {
    const raw = tc.description ?? tc.summary ?? "";
    if (!raw) return "";
    const text = String(raw)
      .replace(/<[^>]*>/g, "")
      .replace(/\s+/g, " ")
      .trim();
    return text;
  })();

  return (
    <HoverTooltip
      width={260}
      delayMs={150}
      estimatedHeight={170}
      align="start"
      alignRatio={alignRatio}
      arrowCardRatio={arrowCardRatio}
      triggerClassName={triggerClassName}
      content={
        <>
          {/* Title */}
          <div className="space-y-0.5">
            <p className="text-[10px] font-medium text-popover-foreground/60">
              TC-{tc.id}
            </p>
            <p className="text-[12px] font-semibold leading-tight text-popover-foreground">
              {tc.name || tc.title || `Test Case ${tc.id}`}
            </p>
          </div>

          {/* Description */}
          {description && (
            <p className="text-[11px] leading-snug text-popover-foreground/70 line-clamp-3">
              {description}
            </p>
          )}

          {/* Type + Status row */}
          <div className="flex items-center gap-2.5 text-[11px]">
            <span className="inline-flex items-center gap-1 text-popover-foreground/70">
              {isAuto ? (
                <Cpu className="h-3 w-3 text-info" />
              ) : (
                <Hand className="h-3 w-3" />
              )}
              {isAuto ? "Automation" : "Manual"}
            </span>
            <span className="inline-flex items-center gap-1">
              <span
                className={`h-1.5 w-1.5 rounded-full ${statusDotClass(effectiveStatus)}`}
              />
              <span className="text-popover-foreground/90">
                {formatStatusLabel(effectiveStatus)}
              </span>
            </span>
          </div>

          {/* Last Run — colored by result */}
          {lastRun && (
            <div className="flex items-center gap-1.5 text-[11px]">
              <span className="font-medium text-popover-foreground/70">
                Last Run:
              </span>
              <span className={`font-medium ${statusColor(lastRun)}`}>
                {formatStatusLabel(lastRun)}
              </span>
            </div>
          )}

          {/* Tags */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.slice(0, 4).map((tag, i) => (
                <Badge
                  key={`${tag}-${i}`}
                  variant="secondary"
                  className="text-[10px] px-1.5 py-0 bg-popover-foreground/10 text-popover-foreground border-transparent hover:bg-popover-foreground/15"
                >
                  {String(tag)}
                </Badge>
              ))}
              {tags.length > 4 && (
                <span className="text-[10px] text-popover-foreground/60">
                  +{tags.length - 4}
                </span>
              )}
            </div>
          )}

          {/* Created by */}
          {createdBy && (
            <div className="flex items-center gap-1.5 text-[11px] text-popover-foreground/70">
              <User className="h-3 w-3" />
              <span>{createdBy}</span>
            </div>
          )}
        </>
      }
    >
      {children}
    </HoverTooltip>
  );
}
